import { supabase } from '../lib/supabase';

/**
 * Get the week range string for a given date
 * Format: "Week of Dec 8-14, 2025"
 */
function getWeekRange(date: Date): string {
  const weekStart = new Date(date);
  weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
  
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6); // End of week (Saturday)
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const startMonth = monthNames[weekStart.getMonth()];
  const startDay = weekStart.getDate();
  const endMonth = monthNames[weekEnd.getMonth()];
  const endDay = weekEnd.getDate();
  const year = weekEnd.getFullYear();
  
  // If same month, show: "Week of Dec 8-14, 2025"
  // If different months, show: "Week of Dec 29-Jan 4, 2025"
  if (startMonth === endMonth) {
    return `Week of ${startMonth} ${startDay}-${endDay}, ${year}`;
  } else {
    return `Week of ${startMonth} ${startDay}-${endMonth} ${endDay}, ${year}`;
  }
}

/**
 * Format date for filename: MM.DD.YYYY
 */
function formatDateForFilename(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}.${day}.${year}`;
}

/**
 * Ensure folder exists or create it
 */
async function ensureFolderExists(
  folderName: string,
  parentFolderId: string | null = null
): Promise<string> {
  // Check if folder already exists
  let query = supabase
    .from('document_folders')
    .select('id')
    .eq('name', folderName);
  
  // Handle null parent_folder_id correctly
  if (parentFolderId === null) {
    query = query.is('parent_folder_id', null);
  } else {
    query = query.eq('parent_folder_id', parentFolderId);
  }
  
  // Use limit(1) to get first matching folder (handles duplicates)
  const { data: existingFolders } = await query.limit(1);

  if (existingFolders && existingFolders.length > 0) {
    console.log(`✅ Found existing folder: ${folderName} (ID: ${existingFolders[0].id})`);
    return existingFolders[0].id;
  }
  
  console.log(`📁 Creating new folder: ${folderName}`);

  // Create folder
  // Mark root VOZACI KONFIRMACIJE folder as system folder (protected from deletion by regular users)
  const isVozaciRootFolder = folderName.startsWith('VOZACI KONFIRMACIJE') && parentFolderId === null;
  
  const { data: newFolder, error } = await supabase
    .from('document_folders')
    .insert({
      name: folderName,
      parent_folder_id: parentFolderId,
      is_system_folder: isVozaciRootFolder,
    })
    .select('id')
    .single();

  if (error) {
    throw new Error(`Failed to create folder "${folderName}": ${error.message}`);
  }

  return newFolder.id;
}

/**
 * Save extra uploads to driver's folder structure with 'Extra Uploads' subfolder
 * Structure: VOZACI KONFIRMACIJE - YYYY / [Driver Name] / Week of [date range] / Extra Uploads / [filename]
 */
export async function saveExtraUploadToDriverFolder(params: {
  originalFilePath: string;
  originalFileName: string;
  driverName: string;
  pickupDate: Date;
}): Promise<{ success: boolean; newFilePath?: string; documentId?: string; error?: string }> {
  try {
    const { originalFilePath, originalFileName, driverName, pickupDate } = params;
    
    // Use pickup date year for folder organization
    const year = pickupDate.getFullYear();
    const rootFolderName = `VOZACI KONFIRMACIJE - ${year}`;
    
    // 1. Ensure root folder exists
    const rootFolderId = await ensureFolderExists(rootFolderName, null);
    
    // 2. Ensure driver folder exists
    const driverFolderId = await ensureFolderExists(driverName, rootFolderId);
    
    // 3. Find existing week folder for this pickup date
    // First, get the expected week folder name
    const expectedWeekFolderName = getWeekRange(pickupDate);
    
    // Check if this week folder already exists
    const { data: existingWeekFolders } = await supabase
      .from('document_folders')
      .select('id, name')
      .eq('parent_folder_id', driverFolderId)
      .ilike('name', 'Week of%')
      .order('created_at', { ascending: false });
    
    let weekFolderId: string;
    let weekFolderName: string;
    
    // Use the most recent week folder if it exists, otherwise create the expected one
    if (existingWeekFolders && existingWeekFolders.length > 0) {
      // Use the most recently created week folder (likely where AI rate conf was saved)
      weekFolderId = existingWeekFolders[0].id;
      weekFolderName = existingWeekFolders[0].name;
      console.log(`📁 Using existing week folder: ${weekFolderName}`);
    } else {
      // No existing week folder, create new one
      weekFolderName = expectedWeekFolderName;
      weekFolderId = await ensureFolderExists(weekFolderName, driverFolderId);
      console.log(`📁 Created new week folder: ${weekFolderName}`);
    }
    
    // 4. Ensure 'Extra Uploads' subfolder exists
    const extraUploadsFolderId = await ensureFolderExists('Extra Uploads', weekFolderId);
    
    // 5. Generate new filepath with sanitized filename
    // Sanitize filename (remove spaces and special characters)
    const sanitizedFileName = originalFileName
      .replace(/\s+/g, '_')  // Replace spaces with underscores
      .replace(/[^\w\-_.]/g, '');  // Remove special characters except dash, underscore, dot
    
    let newFileName = sanitizedFileName;
    let newFilePath = `${rootFolderName}/${driverName}/${weekFolderName}/Extra Uploads/${newFileName}`;
    
    // Check if file already exists and add sequential number if needed
    let counter = 1;
    let fileExists = true;
    
    while (fileExists && counter < 100) {
      const { data: existingFile } = await supabase.storage
        .from('company-documents')
        .list(`${rootFolderName}/${driverName}/${weekFolderName}/Extra Uploads`, {
          search: newFileName
        });
      
      if (existingFile && existingFile.length > 0) {
        // File exists, add counter before extension
        const nameParts = sanitizedFileName.split('.');
        const extension = nameParts.pop();
        const baseName = nameParts.join('.');
        newFileName = `${baseName}-${counter}.${extension}`;
        newFilePath = `${rootFolderName}/${driverName}/${weekFolderName}/Extra Uploads/${newFileName}`;
        counter++;
      } else {
        fileExists = false;
      }
    }
    
    console.log(`📝 Generated extra upload filename: ${newFileName}`);
    
    // 6. Download original file from storage
    const { data: fileData, error: downloadError } = await supabase.storage
      .from('company-documents')
      .download(originalFilePath);
    
    if (downloadError) {
      throw new Error(`Failed to download original file: ${downloadError.message}`);
    }
    
    // 7. Upload to new location
    const { error: uploadError } = await supabase.storage
      .from('company-documents')
      .upload(newFilePath, fileData, {
        cacheControl: '3600',
        upsert: false,
      });
    
    if (uploadError) {
      if (uploadError.message.includes('already exists')) {
        console.log(`File already exists at ${newFilePath}, skipping upload`);
      } else {
        throw new Error(`Failed to upload to new location: ${uploadError.message}`);
      }
    }
    
    // 8. Create document record in database
    const fileExtension = originalFileName.split('.').pop()?.toLowerCase() || '';
    const mimeTypes: Record<string, string> = {
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    };
    
    const { data: docData, error: dbError } = await supabase
      .from('documents')
      .insert({
        name: originalFileName,
        file_name: originalFileName,
        file_path: newFilePath,
        file_size: fileData.size,
        file_type: fileExtension,
        mime_type: mimeTypes[fileExtension] || 'application/octet-stream',
        folder_id: extraUploadsFolderId,
        uploaded_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .select('id')
      .single();

    if (dbError) {
      if (!dbError.message.includes('duplicate')) {
        console.error('Failed to create document record:', dbError);
      }
    }

    console.log(`✅ Extra upload saved to: ${newFilePath}`);

    return {
      success: true,
      newFilePath,
      documentId: docData?.id,
    };
  } catch (error: any) {
    console.error('Error saving extra upload to driver folder:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Repair: scan VOZACI KONFIRMACIJE storage folders and create missing document records
 * This fixes files that were uploaded to storage but failed to get a DB record (due to missing file_name column).
 */
export async function repairMissingDocumentRecords(
  onProgress?: (msg: string) => void
): Promise<{ fixed: number; skipped: number; errors: number }> {
  const log = (msg: string) => {
    console.log(`[Repair] ${msg}`);
    onProgress?.(msg);
  };

  let fixed = 0;
  let skipped = 0;
  let errors = 0;

  const userId = (await supabase.auth.getUser()).data.user?.id;

  try {
    // 1. Find all VOZACI root folders in document_folders
    const { data: rootFolders } = await supabase
      .from('document_folders')
      .select('id, name')
      .ilike('name', 'VOZACI KONFIRMACIJE%')
      .is('parent_folder_id', null);

    if (!rootFolders || rootFolders.length === 0) {
      log('No VOZACI KONFIRMACIJE root folders found.');
      return { fixed, skipped, errors };
    }

    for (const rootFolder of rootFolders) {
      log(`Scanning ${rootFolder.name}...`);

      // 2. Get all driver folders
      const { data: driverFolders } = await supabase
        .from('document_folders')
        .select('id, name')
        .eq('parent_folder_id', rootFolder.id);

      if (!driverFolders) continue;

      for (const driverFolder of driverFolders) {
        // 3. Get all week folders for this driver
        const { data: weekFolders } = await supabase
          .from('document_folders')
          .select('id, name')
          .eq('parent_folder_id', driverFolder.id)
          .ilike('name', 'Week of%');

        if (!weekFolders) continue;

        for (const weekFolder of weekFolders) {
          const storagePath = `${rootFolder.name}/${driverFolder.name}/${weekFolder.name}`;

          // 4. List files in this week folder in storage (rate confirmations)
          const { data: storageFiles } = await supabase.storage
            .from('company-documents')
            .list(storagePath, { limit: 200 });

          if (!storageFiles) continue;

          // Filter out .emptyFolderPlaceholder and folders
          const realFiles = storageFiles.filter(
            f => f.name !== '.emptyFolderPlaceholder' && f.id
          );

          for (const file of realFiles) {
            const filePath = `${storagePath}/${file.name}`;

            // 5. Check if a documents record already exists for this file_path
            const { data: existing } = await supabase
              .from('documents')
              .select('id')
              .eq('file_path', filePath)
              .limit(1);

            if (existing && existing.length > 0) {
              skipped++;
              continue;
            }

            // 6. Create missing document record
            const ext = file.name.split('.').pop()?.toLowerCase() || '';
            const mimeMap: Record<string, string> = {
              pdf: 'application/pdf',
              jpg: 'image/jpeg',
              jpeg: 'image/jpeg',
              png: 'image/png',
            };

            const { error: insertError } = await supabase
              .from('documents')
              .insert({
                name: file.name,
                file_name: file.name,
                file_path: filePath,
                file_size: file.metadata?.size || 0,
                file_type: ext,
                mime_type: mimeMap[ext] || 'application/octet-stream',
                folder_id: weekFolder.id,
                uploaded_by: userId,
              });

            if (insertError) {
              log(`Error creating record for ${filePath}: ${insertError.message}`);
              errors++;
            } else {
              log(`Fixed: ${filePath}`);
              fixed++;
            }
          }

          // 5b. Also check "Extra Uploads" subfolder
          const { data: extraFolders } = await supabase
            .from('document_folders')
            .select('id, name')
            .eq('parent_folder_id', weekFolder.id)
            .eq('name', 'Extra Uploads');

          if (extraFolders && extraFolders.length > 0) {
            const extraFolder = extraFolders[0];
            const extraPath = `${storagePath}/Extra Uploads`;

            const { data: extraFiles } = await supabase.storage
              .from('company-documents')
              .list(extraPath, { limit: 200 });

            if (extraFiles) {
              const realExtraFiles = extraFiles.filter(
                f => f.name !== '.emptyFolderPlaceholder' && f.id
              );

              for (const file of realExtraFiles) {
                const filePath = `${extraPath}/${file.name}`;

                const { data: existing } = await supabase
                  .from('documents')
                  .select('id')
                  .eq('file_path', filePath)
                  .limit(1);

                if (existing && existing.length > 0) {
                  skipped++;
                  continue;
                }

                const ext = file.name.split('.').pop()?.toLowerCase() || '';
                const mimeMap: Record<string, string> = {
                  pdf: 'application/pdf',
                  jpg: 'image/jpeg',
                  jpeg: 'image/jpeg',
                  png: 'image/png',
                };

                const { error: insertError } = await supabase
                  .from('documents')
                  .insert({
                    name: file.name,
                    file_name: file.name,
                    file_path: filePath,
                    file_size: file.metadata?.size || 0,
                    file_type: ext,
                    mime_type: mimeMap[ext] || 'application/octet-stream',
                    folder_id: extraFolder.id,
                    uploaded_by: userId,
                  });

                if (insertError) {
                  log(`Error creating record for ${filePath}: ${insertError.message}`);
                  errors++;
                } else {
                  log(`Fixed: ${filePath}`);
                  fixed++;
                }
              }
            }
          }
        }
      }
    }

    log(`Repair complete: ${fixed} fixed, ${skipped} already OK, ${errors} errors`);
  } catch (error: any) {
    log(`Repair failed: ${error.message}`);
  }

  return { fixed, skipped, errors };
}

/**
 * Copy rate confirmation PDF to driver's folder structure
 * Structure: VOZACI KONFIRMACIJE - YYYY / [Driver Name] / Week of [date range] / [Driver Name]-[pickup date].pdf
 */
export async function saveRateConfirmationToDriverFolder(params: {
  originalFilePath: string;
  driverName: string;
  pickupDate: Date;
  loadNumber: string;
}): Promise<{ success: boolean; newFilePath?: string; error?: string }> {
  try {
    const { originalFilePath, driverName, pickupDate } = params;
    
    // Use pickup date year for folder organization
    const year = pickupDate.getFullYear();
    const rootFolderName = `VOZACI KONFIRMACIJE - ${year}`;
    
    // 1. Ensure root folder exists
    const rootFolderId = await ensureFolderExists(rootFolderName, null);
    
    // 2. Ensure driver folder exists
    const driverFolderId = await ensureFolderExists(driverName, rootFolderId);
    
    // 3. Ensure week folder exists
    const weekFolderName = getWeekRange(pickupDate);
    const weekFolderId = await ensureFolderExists(weekFolderName, driverFolderId);
    
    // 4. Generate new filename: "Driver Name-MM.DD.YYYY.pdf" with sequential numbering if needed
    const dateStr = formatDateForFilename(pickupDate);
    let newFileName = `${driverName}-${dateStr}.pdf`;
    let newFilePath = `${rootFolderName}/${driverName}/${weekFolderName}/${newFileName}`;
    
    // Check if file already exists and add sequential number if needed
    let counter = 1;
    let fileExists = true;
    
    while (fileExists && counter < 100) {
      const { data: existingFile } = await supabase.storage
        .from('company-documents')
        .list(`${rootFolderName}/${driverName}/${weekFolderName}`, {
          search: newFileName
        });
      
      if (existingFile && existingFile.length > 0) {
        // File exists, try next number
        newFileName = `${driverName}-${dateStr}-${counter}.pdf`;
        newFilePath = `${rootFolderName}/${driverName}/${weekFolderName}/${newFileName}`;
        counter++;
      } else {
        fileExists = false;
      }
    }
    
    console.log(`📝 Generated filename: ${newFileName}`);
    
    // 6. Download original file from storage
    const { data: fileData, error: downloadError } = await supabase.storage
      .from('company-documents')
      .download(originalFilePath);
    
    if (downloadError) {
      throw new Error(`Failed to download original file: ${downloadError.message}`);
    }
    
    // 7. Upload to new location
    const { error: uploadError } = await supabase.storage
      .from('company-documents')
      .upload(newFilePath, fileData, {
        cacheControl: '3600',
        upsert: false,
      });
    
    if (uploadError) {
      // If file already exists, that's okay - it means it was already saved
      if (uploadError.message.includes('already exists')) {
        console.log(`File already exists at ${newFilePath}, skipping upload`);
      } else {
        throw new Error(`Failed to upload to new location: ${uploadError.message}`);
      }
    }
    
    // 8. Create document record in database
    const { error: dbError } = await supabase
      .from('documents')
      .insert({
        name: newFileName,
        file_name: newFileName,
        file_path: newFilePath,
        file_size: fileData.size,
        file_type: 'pdf',
        mime_type: 'application/pdf',
        folder_id: weekFolderId,
        uploaded_by: (await supabase.auth.getUser()).data.user?.id,
      });

    if (dbError) {
      // If document already exists, that's okay
      if (!dbError.message.includes('duplicate')) {
        console.error('Failed to create document record:', dbError);
      }
    }

    console.log(`✅ Rate confirmation saved to: ${newFilePath}`);

    return {
      success: true,
      newFilePath,
    };
  } catch (error: any) {
    console.error('Error saving rate confirmation to driver folder:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}
